import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ClubDetailsContext } from '../context/ClubDetailsContext';
import {
    CLEAR_VENUE_SUSPENSION,
    SET_VENUE_SUSPENSION,
} from '../graphql/clubModel/ClubMutation';

const getIndiaDate = () => {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(new Date());
    const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
    return `${values.year}-${values.month}-${values.day}`;
};

const formatDate = value => {
    if (!value) return '';
    const date = new Date(`${value}T00:00:00.000Z`);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('en-IN', {
        day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
    });
};

const getReopenDate = endDate => {
    if (!endDate) return '';
    const date = new Date(`${endDate}T00:00:00.000Z`);
    if (Number.isNaN(date.getTime())) return '';
    date.setUTCDate(date.getUTCDate() + 1);
    return date.toLocaleDateString('en-IN', {
        day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
    });
};

const VenueAvailabilityBanner = ({ management = false }) => {
    const { clubDetails, setClubDetails } = useContext(ClubDetailsContext);
    const today = useMemo(getIndiaDate, []);
    const [showForm, setShowForm] = useState(false);
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);
    const [reason, setReason] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [setVenueSuspension, { loading: scheduling }] = useMutation(SET_VENUE_SUSPENSION);
    const [clearVenueSuspension, { loading: restoring }] = useMutation(CLEAR_VENUE_SUSPENSION);
    const suspension = clubDetails?.suspension;
    const activeByDate = Boolean(
        suspension?.startDate && suspension?.endDate &&
        suspension.startDate <= today && suspension.endDate >= today
    );
    const active = clubDetails?.isSuspended === true || activeByDate;
    const scheduled = Boolean(suspension?.startDate) && !active;
    const venueControlled = suspension?.source === 'VENUE';
    const stateClass = active ? 'is-disabled' : scheduled ? 'is-scheduled' : 'is-open';

    useEffect(() => {
        if (!management) {
            setMessage('');
            setError('');
        }
    }, [
        management,
        suspension?.source,
        suspension?.startDate,
        suspension?.endDate,
        suspension?.reason,
    ]);

    if (!clubDetails?.id) return null;

    if (!management && !active && !scheduled) return null;

    const updateClub = updated => {
        if (updated) setClubDetails(current => ({ ...current, ...updated }));
    };

    const scheduleClosure = async event => {
        event.preventDefault();
        setError('');
        setMessage('');
        const normalizedReason = reason.trim();
        if (!startDate || !endDate) {
            setError('Select both the start and end date.');
            return;
        }
        if (startDate < today) {
            setError('The start date cannot be in the past.');
            return;
        }
        if (endDate < startDate) {
            setError('The end date cannot be before the start date.');
            return;
        }
        if (normalizedReason.length < 3) {
            setError('Enter a reason using at least 3 characters.');
            return;
        }
        try {
            const result = await setVenueSuspension({
                variables: { clubId: clubDetails.id, startDate, endDate, reason: normalizedReason },
            });
            updateClub(result.data?.setVenueSuspension);
            setShowForm(false);
            setReason('');
            setMessage('Venue unavailability scheduled. Active peg holders have been notified.');
        } catch (mutationError) {
            setError(mutationError.message || 'Unable to schedule venue unavailability.');
        }
    };

    const restoreVenue = async () => {
        setError('');
        setMessage('');
        try {
            const result = await clearVenueSuspension({ variables: { clubId: clubDetails.id } });
            updateClub(result.data?.clearVenueSuspension);
            if (management) setMessage('Venue availability restored.');
        } catch (mutationError) {
            setError(mutationError.message || 'Unable to restore venue availability.');
        }
    };

    return (
        <section
            className={`${management ? 'venue-availability-settings' : 'venue-availability-banner'} ${stateClass}`}
            aria-label={
                management
                    ? 'Venue availability settings'
                    : active ? 'Venue disabled' : 'Venue unavailability scheduled'
            }
        >
            <div className="venue-availability-summary">
                <div>
                    <span className="venue-availability-kicker">Venue availability</span>
                    <strong>
                        {active ? 'Temporarily unavailable' : scheduled ? 'Unavailability scheduled' : 'Venue is open'}
                    </strong>
                    {suspension ? (
                        <span className="venue-availability-detail">
                            {formatDate(suspension.startDate)}–{formatDate(suspension.endDate)} · {suspension.reason}
                            {active ? ` · Reopens ${getReopenDate(suspension.endDate)}` : ''}
                        </span>
                    ) : (
                        <span className="venue-availability-detail">Customers can discover and use this venue.</span>
                    )}
                    {suspension && !venueControlled ? (
                        <span className="venue-availability-owner">
                            Disabled by BoozeOn admin. Only BoozeOn admin can reactivate this venue.
                        </span>
                    ) : null}
                </div>
                <div className="venue-availability-actions">
                    {management && !suspension && (
                        <button type="button" onClick={() => setShowForm(open => !open)}>
                            Temporarily disable venue
                        </button>
                    )}
                    {active && venueControlled && (
                        <label className="venue-enabled-switch">
                            <span>Venue enabled</span>
                            <input
                                type="checkbox"
                                role="switch"
                                aria-label="Venue enabled"
                                checked={false}
                                disabled={restoring}
                                onChange={event => {
                                    if (event.target.checked) restoreVenue();
                                }}
                            />
                            <span className="venue-enabled-switch-track" aria-hidden="true" />
                        </label>
                    )}
                    {management && scheduled && venueControlled && (
                        <button type="button" onClick={restoreVenue} disabled={restoring}>
                            {restoring ? 'Cancelling…' : 'Cancel scheduled unavailability'}
                        </button>
                    )}
                </div>
            </div>

            {management && showForm && !suspension && (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                <form className="venue-availability-form" onSubmit={scheduleClosure}>
                    <DatePicker
                        label="From"
                        value={dayjs(startDate)}
                        minDate={dayjs(today)}
                        format="DD/MM/YYYY"
                        openOnFieldClick
                        onChange={value => {
                            if (!value?.isValid()) return;
                            const nextDate = value.format('YYYY-MM-DD');
                            setStartDate(nextDate);
                            if (endDate < nextDate) setEndDate(nextDate);
                        }}
                        slotProps={{
                            textField: { className: 'hh-picker-field venue-availability-date-picker' },
                            desktopPaper: { className: 'hh-calendar-popup' },
                        }}
                    />
                    <DatePicker
                        label="Through"
                        value={dayjs(endDate)}
                        minDate={dayjs(startDate || today)}
                        format="DD/MM/YYYY"
                        openOnFieldClick
                        onChange={value => {
                            if (value?.isValid()) setEndDate(value.format('YYYY-MM-DD'));
                        }}
                        slotProps={{
                            textField: { className: 'hh-picker-field venue-availability-date-picker' },
                            desktopPaper: { className: 'hh-calendar-popup' },
                        }}
                    />
                    <label className="venue-availability-reason">
                        Reason
                        <input
                            type="text" value={reason} maxLength={500}
                            aria-invalid={Boolean(error && reason.trim().length < 3)}
                            placeholder="e.g. Private event or maintenance"
                            onChange={event => setReason(event.target.value)}
                        />
                    </label>
                    <button type="submit" disabled={scheduling}>
                        {scheduling ? 'Scheduling…' : 'Confirm unavailability'}
                    </button>
                    <button type="button" className="secondary" onClick={() => setShowForm(false)}>
                        Cancel
                    </button>
                    <p className="venue-availability-note">
                        Active peg holders will be notified and their validity extended for every disabled day.
                    </p>
                </form>
                </LocalizationProvider>
            )}
            {message && <p className="venue-availability-feedback success" role="status">{message}</p>}
            {error && <p className="venue-availability-feedback error" role="alert">{error}</p>}
        </section>
    );
};

export default VenueAvailabilityBanner;
